//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import {
  PinterestAccountAudienceStats,
  PinterestAccountAudienceSummaryStats,
} from '@bvengineering/pinterest-gateway-service-types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as pinterestAnalyticsApi from '@apis/pinterest-analytics/pinterestAnalyticsApi';
import {
  IAliasedDateRange,
  getAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { PinterestAudienceByLocationOptions } from '@pinterest/components/audience/pinterestAudienceConstants';
import { IPinterestAudiencePageState } from '@pinterest/redux/audience/PinterestAudienceModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';

import { crl8 } from 'app/curalate.globals';

const initialState: IPinterestAudiencePageState = {
  summaryStats: undefined,
  followerCountStats: {
    dateRange: getAliasedDateRange(
      'last30Days',
      crl8.session?.timeZone.id ?? TimeZoneId.Utc,
    ),
    dailyStats: undefined,
  },
  audienceByLocationOption: PinterestAudienceByLocationOptions.topMetros,
  audienceMetrics: {
    audienceMetro: {
      'Montreal, Quebec': 870,
      'Anaheim, California': 935,
      'Indianapolis, Indiana': 1423,
      'Tampa, Florida': 1899,
      'Raleigh, North Carolina': 1362,
      'Buffalo, New York': 898,
      'Detroit, Michigan': 1536,
      'Miami, Florida': 1860,
      'Denver, Colorado': 1011,
      'Toronto, Ontario': 1727,
      'San Antonio, Texas': 1971,
    },
    audienceCountry: {
      MA: 770,
      ES: 4418,
      PR: 5981,
      US: 474494,
    },
    audienceGenderAge: {
      'U.13-17': 57,
      'U.55-64': 221,
      'M.55-64': 1296,
      'U.35-44': 1368,
      'F.55-64': 25321,
      'M.65+': 749,
      'M.25-34': 15736,
      'F.13-17': 8713,
      'M.18-24': 7516,
      'F.25-34': 277630,
      'M.45-54': 4039,
      'U.45-54': 683,
      'M.35-44': 8681,
      'U.18-24': 351,
      'F.35-44': 178325,
      'U.25-34': 1359,
      'U.65+': 117,
      'F.45-54': 84405,
      'F.65+': 8126,
      'F.18-24': 84925,
      'M.13-17': 715,
    },
    audienceDevice: {
      iPhone: 35,
      iPad: 5,
      'Android Mobile': 32,
      'Android Tablet': 4,
      'Mobile Web': 21,
      Web: 26,
    },
  },
  flags: {
    isFetchingSummaryStats: false,
    isFetchingAudienceDailyStats: false,
    isFetchingAudienceMetrics: false,
  },
  errors: {
    fetchSummaryStatsError: undefined,
    fetchAudienceDailyStatsError: undefined,
    fetchAudienceMetricsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.PinterestAudience,
  initialState,
  reducers: {
    requestSummaryStats(state) {
      state.flags.isFetchingSummaryStats = true;
      state.errors.fetchSummaryStatsError = undefined;
    },
    receiveSummaryStatsSucceeded(
      state,
      action: PayloadAction<PinterestAccountAudienceSummaryStats>,
    ) {
      state.flags.isFetchingSummaryStats = false;
      state.summaryStats = { ...action.payload };
    },
    receiveSummaryStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingSummaryStats = false;
      state.errors.fetchSummaryStatsError = action.payload;
    },
    requestFollowersDailyStats(state) {
      state.flags.isFetchingAudienceDailyStats = true;
      state.errors.fetchAudienceDailyStatsError = undefined;
    },
    receiveFollowersDailyStatsSucceeded(
      state,
      action: PayloadAction<PinterestAccountAudienceStats>,
    ) {
      state.flags.isFetchingAudienceDailyStats = false;
      state.followerCountStats.dailyStats = { ...action.payload.dailyMetrics };
    },
    receiveFollowersDailyStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingAudienceDailyStats = false;
      state.errors.fetchAudienceDailyStatsError = action.payload;
    },
    requestAudienceMetrics(state) {
      state.flags.isFetchingAudienceMetrics = true;
      state.errors.fetchAudienceMetricsError = undefined;
    },
    receiveAudienceMetricsSucceeded(state) {
      state.flags.isFetchingAudienceMetrics = false;
    },
    receiveAudienceMetricsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingAudienceMetrics = false;
      state.errors.fetchAudienceMetricsError = action.payload;
    },
    updateFollowerCountDateRange(
      state,
      action: PayloadAction<IAliasedDateRange>,
    ) {
      state.followerCountStats.dateRange = action.payload;
    },
    updateAudienceByLocationOption(
      state,
      action: PayloadAction<PinterestAudienceByLocationOptions>,
    ) {
      state.audienceByLocationOption = action.payload;
    },
  },
});

export const fetchAudienceSummaryStats = createThunk(
  () => async (dispatch, getState) => {
    const pinterestAccountId = getState().session.pinterestUserId;

    dispatch(actions.requestSummaryStats());

    try {
      const summaryStatsData =
        await pinterestAnalyticsApi.getAudienceSummaryMetrics({
          pinterestAccountId,
        });

      dispatch(actions.receiveSummaryStatsSucceeded(summaryStatsData.data));
    } catch (_e) {
      dispatch(
        actions.receiveSummaryStatsFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const fetchFollowersDailyStats = createThunk(
  () => async (dispatch, getState) => {
    const {
      followerCountStats: { dateRange },
    } = getState().pinterest.audience;
    const pinterestAccountId = getState().session.pinterestUserId;
    const userTimeZone = getUserTimeZone(getState());

    dispatch(actions.requestFollowersDailyStats());

    try {
      const statsData = await pinterestAnalyticsApi.getAudienceDailyMetrics({
        pinterestAccountId,
        startTime: DateString.toDate(dateRange.startDate, userTimeZone),
        endTime: DateString.toDate(dateRange.endDate, userTimeZone),
      });

      dispatch(actions.receiveFollowersDailyStatsSucceeded(statsData.data));
    } catch (_e) {
      dispatch(
        actions.receiveFollowersDailyStatsFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const updateDailyStatsDateRangeAndRefresh = createThunk(
  (dateRange: IAliasedDateRange) => async (dispatch) => {
    dispatch(actions.updateFollowerCountDateRange(dateRange));
    void dispatch(fetchFollowersDailyStats());
  },
);

export const { updateAudienceByLocationOption } = actions;

export default reducer;
